import type { Transport } from './transport';

export interface QueueConfig {
  transport: Transport;
  flushIntervalMs: number;
  batchSize: number;
  maxRetries?: number;
  onError?: (error: Error) => void;
}

const RETRY_MAX_DELAY_MS = 30000;

/**
 * Buffer de eventos con envío en batch, flush por intervalo o por tamaño, y
 * reintentos con backoff exponencial ante fallos de red. Los eventos que fallan
 * se re-encolan al inicio para preservar el orden.
 */
export class Queue<T = unknown> {
  private buffer: T[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private flushing = false;
  private retryCount = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly config: QueueConfig) {
    this.startTimer();
  }

  /** Encola un evento; dispara flush si se alcanza `batchSize`. */
  add(item: T): void {
    this.buffer.push(item);
    if (this.buffer.length >= this.config.batchSize) {
      void this.flush();
    }
  }

  /** Flush manual vía `fetch`. Resuelve cuando termina (los retries son diferidos). */
  async flush(): Promise<void> {
    if (this.flushing || this.buffer.length === 0) return;

    this.flushing = true;
    const batch = this.buffer.splice(0, this.buffer.length);

    try {
      await this.config.transport.send('/v1/events', { events: batch });
      this.retryCount = 0;
    } catch (error) {
      // Re-encolar al INICIO para preservar el orden cronológico.
      this.buffer.unshift(...batch);
      this.scheduleRetry();
      this.config.onError?.(error as Error);
    } finally {
      this.flushing = false;
    }
  }

  /** Flush síncrono vía `sendBeacon`, para `pagehide`/`visibilitychange`. */
  flushBeacon(): void {
    if (this.buffer.length === 0) return;
    const batch = this.buffer.splice(0, this.buffer.length);
    const success = this.config.transport.sendBeacon('/v1/events', { events: batch });
    if (!success) {
      // La página se está cerrando; re-encolar es poco útil pero evita pérdida si no se cierra.
      this.buffer.unshift(...batch);
    }
  }

  /** Detiene timers internos. Llamar al destruir el cliente. */
  destroy(): void {
    if (this.timer) clearInterval(this.timer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.timer = null;
    this.retryTimer = null;
  }

  private startTimer(): void {
    this.timer = setInterval(() => {
      void this.flush();
    }, this.config.flushIntervalMs);
  }

  private scheduleRetry(): void {
    const maxRetries = this.config.maxRetries ?? 3;
    if (this.retryCount >= maxRetries) {
      this.retryCount = 0;
      return;
    }

    this.retryCount++;
    // Backoff exponencial: 1s, 2s, 4s, 8s... con tope de 30s, MÁS jitter.
    // Sin jitter, ante un 5xx del backend TODOS los clientes reintentan en
    // lockstep (1s, 2s, 4s…) y amplifican el pico justo cuando está degradado.
    // Equal jitter: mitad fija + mitad aleatoria → desincroniza la horda.
    const base = Math.min(1000 * 2 ** (this.retryCount - 1), RETRY_MAX_DELAY_MS);
    const delayMs = base / 2 + Math.random() * (base / 2);

    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      void this.flush();
    }, delayMs);
  }
}
